import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IRack, RackingService, WorkCenterFilterMode } from '@shared/services/racking.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-racking-create-update-rack-dialog',
	templateUrl: './racking-create-update-rack.html',
	styleUrls: ['./racking-create-update-rack.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RackingCreateUpdateRackDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	form: FormGroup;
	rack: IRack;
	httpError = '';
	loading = false;
	// Declaration order is the select's order, Ignore first: it is the resting state.
	public readonly workCenterFilterModes: WorkCenterFilterMode[] = Object.values(WorkCenterFilterMode);
	// The key being typed, before it becomes a chip. Outside this.form so it never reaches
	// the saved rack, but it blocks Save while refused, so a typed key is not lost.
	public workCenterInput: FormControl<string>;
	// Material reads the error state from the chip grid, but the validators live on the input
	// and on the key list behind the chips.
	public readonly workCenterEntryErrorState: ErrorStateMatcher = {
		isErrorState: () => this.workCenterInput.invalid || this.form.controls['workCenterFilterKeys'].invalid,
	};
	translationComponents = [{ type: { self: '/racking/component-types/frame' } }, { type: { self: '/racking/component-types/sash' } }, { type: { self: '/racking/component-types/jambs' } }, { type: { self: '/racking/component-types/muntin' } }];

	constructor(
		@Inject(MAT_DIALOG_DATA) public editRack: IRack,
		public dialogRef: MatDialogRef<RackingCreateUpdateRackDialogComponent>,
		private rackingService: RackingService,
		private fb: FormBuilder,
	) {
		this.rack = {
			self: null,
			name: null,
			description: null,
			updatedDate: null,
			updatedBy: null,
			rows: null,
			columns: null,
			components: null,
			translationComponents: null,
			width: null,
			height: null,
			depth: null,
			capacity: null,
			workCenterFilterMode: WorkCenterFilterMode.Ignore,
			workCenterFilterKeys: [],
		};

		if (editRack) {
			this.rack = { ...editRack };
		}

		if (!this.rack.workCenterFilterMode) {
			this.rack.workCenterFilterMode = WorkCenterFilterMode.Ignore;
		}
	}

	public ngOnInit(): void {
		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: [this.rack.name, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			description: [this.rack.description, [Validators.maxLength(100), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			rows: [this.rack.rows, [Validators.required, Validators.min(0), Validators.max(100)]],
			columns: [this.rack.columns, [Validators.required, Validators.min(0), Validators.max(100)]],
			width: [this.rack.width, [Validators.min(0), Validators.max(2147483647)]],
			height: [this.rack.height, [Validators.min(0), Validators.max(2147483647)]],
			depth: [this.rack.depth, [Validators.min(0), Validators.max(2147483647)]],
			capacity: [this.rack.capacity, [Validators.required, Validators.min(0), Validators.max(2147483647)]],
			components: [this.rack.components, [Validators.required]],
			workCenterFilterMode: [this.rack.workCenterFilterMode],
			// A plain string[] so subscribeToFormChanges passes it through untouched.
			workCenterFilterKeys: this.fb.control<string[]>(this.rack.workCenterFilterKeys ?? []),
		});

		this.workCenterInput = this.fb.nonNullable.control('', [validateWorkCenterEntry(() => this.workCenterFilterKeys)]);

		// Attached after the group exists, so the validator can read the mode alongside it.
		const keys = this.form.controls['workCenterFilterKeys'];
		keys.addValidators(validateWorkCenterFilter(() => this.form.controls['workCenterFilterMode'].value));
		keys.updateValueAndValidity();

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.rack) {
					this.rack[key] = value[key];
				}
			});
		});
		this.form.controls['workCenterFilterMode'].valueChanges.subscribe((mode) => {
			if (mode === WorkCenterFilterMode.Ignore) {
				// Ignore omits the filter on save, so the chips go with it.
				this.setWorkCenterFilterKeys([]);
				this.workCenterInput.updateValueAndValidity();
			}
			// the mode decides whether an empty key list is allowed
			this.form.controls['workCenterFilterKeys'].updateValueAndValidity();
		});
	}

	public get controls() {
		return this.form.controls;
	}

	public get workCenterFilterKeys(): string[] {
		return this.form.controls['workCenterFilterKeys'].value ?? [];
	}

	/** Commits the typed key as a chip. Refused input stays in the input, unchanged. */
	public addWorkCenter(): void {
		const key = this.workCenterInput.value;
		if (key === '' || this.workCenterInput.invalid) {
			return;
		}
		this.setWorkCenterFilterKeys([...this.workCenterFilterKeys, key]);
		this.workCenterInput.setValue('');
		// Guarded, not subscribed: only the first chip picks a mode.
		const mode = this.form.controls['workCenterFilterMode'];
		if (mode.value === WorkCenterFilterMode.Ignore) {
			mode.setValue(WorkCenterFilterMode.Include);
		}
	}

	public removeWorkCenter(key: string): void {
		this.setWorkCenterFilterKeys(this.workCenterFilterKeys.filter((committed) => committed !== key));
		// removing a chip does not re-run the validators, so the freed key stays a duplicate
		this.workCenterInput.updateValueAndValidity();
	}

	private setWorkCenterFilterKeys(keys: string[]): void {
		// A new array every time: this.rack shares this one with the table row behind the dialog.
		this.form.controls['workCenterFilterKeys'].setValue(keys);
	}

	public onCancel(): void {
		this.dialogRef.close();
	}

	public async onSave() {
		this.httpError = '';
		// The field commits on blur, but a key freed by removing its duplicate chip is left
		// valid and uncommitted with the focus elsewhere. Commit it here rather than saving
		// a rack without the key the user can still see in the field.
		this.addWorkCenter();
		if (this.form.invalid || this.workCenterInput.invalid || this.loading) return;
		this.loading = true;

		const saveObservable = this.rack.self ? this.rackingService.updateRack(this.rack) : this.rackingService.createRack(this.rack);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
		});
	}

	getComponent(url: string): string {
		return this.rackingService.getLastSegment(url);
	}
}

/**
 * Refuses entries the rack could never match on. Duplicates are the only rule the API itself rejects; the rest come from the profile side, where a profile's work centres are read out of a comma-separated, trimmed aggregate and compared ordinally. A key carrying a comma or padding therefore matches nothing - and "A1"
 * and "a1" are two distinct work centres.
 */
function validateWorkCenterEntry(committed: () => string[]): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const key: string = control.value ?? '';
		if (key === '') {
			return null;
		}
		if (key.trim() === '') {
			return { workCenterWhitespace: true };
		}
		if (key.includes(',')) {
			return { workCenterComma: true };
		}
		if (key !== key.trim()) {
			return { workCenterPadded: true };
		}
		if (committed().includes(key)) {
			return { workCenterDuplicate: true };
		}
		return null;
	};
}

/** The API's own rule: it refuses a filter carrying no work centres - "Work centers cannot be empty, omit workCenters instead" - so a chosen mode has to name at least one. Ignore is how a rack is saved with no filter at all. */
function validateWorkCenterFilter(mode: () => WorkCenterFilterMode): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const keys: string[] = control.value ?? [];
		if (mode() === WorkCenterFilterMode.Ignore || keys.length > 0) {
			return null;
		}
		return { workCenterFilterEmpty: true };
	};
}
